import { ContentNugget, Tool } from '@backrs_ui/common-ui';

import {
  SEARCH_TYPES,
  searchForContentNuggets,
  SearchResult,
  setSearchLoading,
} from '@backrs_ui/grow-ui/src/redux/search/search';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';

import {
  ALL_SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_INTERNSHIPS,
  SEARCH_CATEGORIES_SCHOLARSHIPS,
} from './search.filters';
import {
  careerPathTransform,
  competencyTransform,
  internshipTransform,
  profileTransform,
  scholarshipTransform,
  externalProfileTransform,
  collegeTransform,
  employerTransform,
} from './search.nuggetTransformers';
import { SEARCH_PAGE_CATEGORIES, SearchCategory, SearchPageCategory } from './search.types';

export const fetchNuggetsUserHasSearched = async (
  dispatch: AppDispatch,
  search_term: string,
  page: number,
  types?: SearchCategory[],
  category: SearchPageCategory = SEARCH_PAGE_CATEGORIES.DEFAULT as SearchPageCategory,
) => {
  try {
    await dispatch(searchForContentNuggets({ search_term, page, types, category }));
  } catch (error) {
    console.error('Failed to fetch content nuggets:', error);
  }
};

type HandleInitiateSearchProps = Readonly<{
  dispatch: AppDispatch;
  currentSearchTerm: string;
  pageNum: number;
  items: SearchCategory[];
  searchPageCategory: SearchPageCategory;
  hasInitialSearchBeenMade?: boolean;
}>;

export const handleInitiateSearch = ({
  dispatch,
  currentSearchTerm,
  pageNum,
  items,
  searchPageCategory,
  hasInitialSearchBeenMade, // we assume the initial search has been made
}: HandleInitiateSearchProps) => {
  // Only initiate search if there's a search term OR if there are selected filter items
  if (
    (!currentSearchTerm || currentSearchTerm === '') &&
    (!items || items.length === 0) &&
    hasInitialSearchBeenMade
  ) {
    return;
  }

  dispatch(setSearchLoading(true));

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS) {
    const scholarshipPageSelectedItems = items?.length
      ? items
      : [
          !hasInitialSearchBeenMade
            ? SEARCH_CATEGORIES_SCHOLARSHIPS.PRE_FILTERED
            : ALL_SEARCH_CATEGORIES.SCHOLARSHIPS_SCHOLARSHIPS,
        ];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      scholarshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.INTERNSHIPS) {
    const nonItemSearch = !hasInitialSearchBeenMade
      ? [SEARCH_CATEGORIES_INTERNSHIPS.JOB_TYPE_ALL]
      : [
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS,
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS,
        ];

    const internshipPageSelectedItems = items?.length ? items : nonItemSearch;

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      internshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.COLLEGES) {
    const universityPageSelectedItems = items?.length ? items : [ALL_SEARCH_CATEGORIES.COLLEGE_ALL];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      universityPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  fetchNuggetsUserHasSearched(
    dispatch,
    currentSearchTerm || '',
    pageNum,
    items,
    searchPageCategory,
  );
};

export function transformSearchResultsToNuggets(searchResult: SearchResult['results']): {
  nuggets: ContentNugget[];
  tools: Tool[];
} {
  const mappedTools: Tool[] = searchResult
    ?.map(({ result }) => (result.type === SEARCH_TYPES.TOOL ? result.tool : null))
    .filter((tool) => tool !== null);

  const mappedResults: ContentNugget[] = (searchResult || [])
    .map(({ result }) => {
      switch (result.type) {
        case SEARCH_TYPES.TOOL:
          return null;

        case SEARCH_TYPES.COMPETENCY:
          return competencyTransform(result.competency);

        case SEARCH_TYPES.SCHOLARSHIP:
          return scholarshipTransform(result.scholarship);

        case SEARCH_TYPES.PROFILE:
          return profileTransform(result.profile);

        case SEARCH_TYPES.CAREER_PATH:
          return careerPathTransform(result.career_path);

        case SEARCH_TYPES.INTERNSHIP:
          return internshipTransform(result.internship);

        case SEARCH_TYPES.EXTERNAL_PROFILE:
          return externalProfileTransform(result.external_profile);

        case SEARCH_TYPES.COLLEGE:
          return collegeTransform(result.college);

        case SEARCH_TYPES.EMPLOYER:
          return employerTransform(result.employer);

        case SEARCH_TYPES.NUGGET:
          return {
            ...result.nugget,
          };

        default:
          return undefined;
      }
    })
    .filter((result) => !!result);

  return { nuggets: mappedResults, tools: mappedTools };
}
